import { Heading, Stack } from "@chakra-ui/react";
import { FC, useEffect, useRef, useState } from "react";
import {
  Control,
  FieldValues,
  UseFormResetField,
  UseFormWatch,
} from "react-hook-form";
import {
  borderRadius,
  borderStyle,
  conditionalFieldStyles,
} from "../../../utils/styles/styles";
import FormSelect from "../../../components/molecule/Select/FormSelect";
import { Option } from "../../../types/General/Option";
import { getRole } from "../../../services/getRole";
import { getPartners } from "../../../services/TabelasAuxiliares/getPartner";
import { getProvince } from "../../../services/TabelasAuxiliares/getProvince";
import { getCountyById } from "../../../services/TabelasAuxiliares/getCountyById";
import { Toast } from "../../../components/atom/Toast/Toast";
import { useSkipFirstRender } from "../../../hooks/useSkipFirstRender";
import { getOrganization } from "../../../services/getOrganization";

interface SystemDataFormProps {
  errors: any;
  control: Control<any>;
  watch: UseFormWatch<FieldValues>;
  isDisabled?: boolean;
  resetField: UseFormResetField<FieldValues>;
  isFormInitialized?: boolean;
}

const SystemDataForm: FC<SystemDataFormProps> = ({
  errors,
  control,
  watch,
  isDisabled,
  resetField,
  isFormInitialized = true,
}) => {
  const { addToast } = Toast();
  const [organizationOptions, setOrganizationOptions] = useState<Option[]>([]);
  const [organizationDisabled, setOrganizationDisabled] = useState(true);
  const selectedRole = isFormInitialized ? watch("roleOption") : undefined;
  const selectedProvince = isFormInitialized
    ? watch("provinceOption")
    : undefined;
  const prevSelectedProvince = useRef(selectedProvince);
  const selectedCounty = isFormInitialized ? watch("countyOption") : undefined;
  const selectedOrganization = isFormInitialized
    ? watch("organizationOption")
    : undefined;

  const skipFirstRender = useSkipFirstRender();
  useEffect(() => {
    if (!isFormInitialized) return;

    if (skipFirstRender()) {
      if (selectedOrganization) {
        resetField("organizationOption", {
          defaultValue: selectedOrganization,
        });
      }
      return;
    }
    if (selectedRole) {
      resetField("provinceOption");
      resetField("countyOption");
      resetField("partnerOption");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRole]);

  useEffect(() => {
    if (prevSelectedProvince.current && !selectedProvince) {
      resetField("countyOption");
    }
    prevSelectedProvince.current = selectedProvince;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProvince]);

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const result = await getOrganization();

        if (selectedRole) {
          const filteredOptions = filterOrganizations(result.content);
          setOrganizationOptions(filteredOptions);
          resetField("organizationOption");
        } else {
          setOrganizationDisabled(true);
        }
      } catch (error) {
        addToast({
          status: "error",
          title: "Error",
          description: "Ocorreu um erro ao buscar os dados",
        });
        console.error("API Error:", error);
      }
    };

    fetchOrganizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRole, selectedProvince, selectedCounty]);

  const filterOrganizations = (organizations: any) => {
    if (selectedRole?.level.id <= 3) {
      setOrganizationDisabled(false);
      return organizations;
    }
    if ([4].includes(selectedRole?.level.id) && selectedProvince) {
      setOrganizationDisabled(false);
      return organizations.filter(
        (item: any) => item.province?.id === selectedProvince.id,
      );
    }
    if ([5].includes(selectedRole?.level.id) && selectedCounty) {
      setOrganizationDisabled(false);
      return organizations.filter(
        (item: any) => item.county?.id === selectedCounty.id,
      );
    }
    setOrganizationDisabled(true);
    return [];
  };

  return (
    <Stack
      spacing={4}
      border={borderStyle}
      borderRadius={borderRadius}
      background="gray.50"
      p={6}
    >
      <Heading as="h3" fontSize="2xl">
        Sistema
      </Heading>

      <FormSelect
        control={control}
        errors={errors}
        watch={watch}
        fieldName="roleOption"
        fieldLabel="Perfil"
        fetchOptions={getRole}
        isDisabled={isDisabled}
      />
      {selectedRole?.level?.id === 2 ? (
        <FormSelect
          control={control}
          errors={errors}
          watch={watch}
          fieldName="partnerOption"
          fieldLabel="Parceiro"
          fetchOptions={getPartners}
          isDisabled={isDisabled}
          additionalStyle={conditionalFieldStyles}
        />
      ) : (
        <>
          <FormSelect
            control={control}
            errors={errors}
            watch={watch}
            fieldName="provinceOption"
            fieldLabel="Província"
            fetchOptions={getProvince}
            dependencyWatchField="roleOption"
            dependencyCondition={(selectedLevel) =>
              selectedLevel?.level.id >= 4 && selectedRole !== null
            }
            isRequiredCondition={(selectedLevel) =>
              selectedLevel?.level.id >= 4 && selectedRole !== null
            }
            isDisabled={
              isDisabled ||
              selectedRole === undefined ||
              selectedRole?.level.id <= 3
            }
            additionalStyle={conditionalFieldStyles}
            defaultValue={undefined}
          />
          <FormSelect
            control={control}
            errors={errors}
            watch={watch}
            fieldName="countyOption"
            fieldLabel="Município"
            fetchOptions={() =>
              getCountyById({ ParentId: selectedProvince?.id })
            }
            dependencyWatchField="roleOption"
            dependencyCondition={(selectedProvince) => !!selectedProvince}
            isRequiredCondition={(selectedLevel) =>
              selectedLevel?.level.id === 5 && selectedRole !== null
            }
            isDisabled={
              isDisabled ||
              selectedRole === null ||
              !selectedProvince ||
              selectedRole?.level.id !== 5
            }
            additionalStyle={conditionalFieldStyles}
            defaultValue={undefined}
          />
          <FormSelect
            control={control}
            errors={errors}
            watch={watch}
            fieldName="organizationOption"
            fieldLabel="Entidade"
            isDisabled={isDisabled || organizationDisabled}
            selectOptions={organizationOptions}
            additionalStyle={{ ...conditionalFieldStyles, ml: "5", pr: "5" }}
            defaultValue={undefined}
          />
        </>
      )}
    </Stack>
  );
};

export default SystemDataForm;
